import { useState, useRef, useCallback, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useEventCallback } from '../../hooks/use-event-callback';
import { useCastController } from './use-cast-controller';

export interface MediaStateOptions<T> {
  initialValue: T;
  onControlDone: (value: T) => void;
  onEvent?: (event: cast.framework.RemotePlayerChangedEvent) => T | undefined;
  throttleMs?: number;
}

export function useMediaState<T = undefined>({
  initialValue,
  onControlDone,
  onEvent,
  throttleMs = 750,
}: MediaStateOptions<T>) {
  const [value, setValue] = useState<T>(initialValue);
  const [isControlling, setIsControlling] = useState(false);
  const throttle = useRef<NodeJS.Timeout | undefined>();
  const onDoneCallback = useEventCallback(onControlDone);

  const controller = useCastController();

  const scheduleDoneCallback = useCallback(
    (updatedValue: T) => {
      clearTimeout(throttle.current);
      throttle.current = setTimeout(() => {
        onDoneCallback(updatedValue);
        setIsControlling(false);
      }, throttleMs);
    },
    [onDoneCallback, throttleMs],
  );

  const controlValue = useCallback(
    (action: React.SetStateAction<T>) => {
      unstable_batchedUpdates(() => {
        setIsControlling(true);
        setValue((prevValue) => {
          if (action instanceof Function) {
            const updatedValue = action(prevValue);
            scheduleDoneCallback(updatedValue);
            return updatedValue;
          }

          scheduleDoneCallback(action);
          return action;
        });
      });
    },
    [scheduleDoneCallback],
  );

  const onEventCallback = useEventCallback((event: cast.framework.RemotePlayerChangedEvent) => {
    if (onEvent) {
      const updatedValue = onEvent(event);
      if (updatedValue !== undefined && !isControlling) {
        setValue(updatedValue);
      }
    }
  });

  useEffect(() => {
    controller.addEventListener(window.cast.framework.RemotePlayerEventType.ANY_CHANGE, onEventCallback);

    return () => {
      controller.removeEventListener(window.cast.framework.RemotePlayerEventType.ANY_CHANGE, onEventCallback);
    };
  }, [onEventCallback, controller]);

  return [value, controlValue] as const;
}
